<template>
  <Modal
    class="simple-modal"
    :is-displayed="isDisplayed"
    v-bind="$attrs"
    :modal-shortcuts="modalShortcuts"
    @close="close"
  >
    <template #header>
      {{ header }}
    </template>
    <p>
      {{ text }}
    </p>
    <FormInput
      v-if="input"
      v-model:value="inputValue"
      type="text"
      :placeholder="input.placeholder"
      :state="inputError ? 'error' : 'default'"
      @keydown.enter="validatePrimaryAction"
    />
    <div v-if="actions && actions.length > 0" class="buttons">
      <BaseButton
        v-for="action in actions"
        :key="action.name"
        kind="important"
        :type="action.type || 'primary'"
        :tint="action.tint || 'none'"
        :text="action.name"
        :state="
          action.disabled || (action.type === 'primary' && !isInputValid)
            ? 'disabled'
            : 'default'
        "
        :tooltip="{
          shortcut:
            action.name === 'Cancel'
              ? modalActions.closeModal.shortcutsDisplay
              : action.name === 'Delete' || action.name === 'Discard'
                ? modalActions.validateModal.shortcutsDisplay
                : undefined,
        }"
        tooltip-display="always"
        @click="
          () => {
            if (action.disabled || (action.type === 'primary' && !isInputValid))
              return;
            action.func();
            close();
          }
        "
      >
        <template v-if="action.icon" #icon>
          <component :is="action.icon" />
        </template>
      </BaseButton>
    </div>
  </Modal>
</template>

<script lang="ts">
import {
  Component,
  computed,
  defineComponent,
  onBeforeUnmount,
  Ref,
  ref,
  ShallowRef,
  shallowRef,
  watch,
} from "vue";

import { modalActions } from "@/components/actions/actions";
import BaseButton, { ButtonType } from "@/components/base/BaseButton.vue";
import { Tint } from "@/components/base/types";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import { SimpleModalContent } from "@/components/modal";
import { ShortcutList, validateForm } from "@/components/shortcuts";
import { useOverlayPresence } from "@/composables/useOverlayPresence";

import Modal from "./Modal.vue";

export default defineComponent({
  components: {
    BaseButton,
    FormInput,
    Modal,
  },
  setup() {
    const header = ref("");
    const text = ref("");
    const input: ShallowRef<SimpleModalContent["input"] | null> =
      shallowRef(null);
    const inputValue = ref("");
    const inputError = ref(false);
    const actions: ShallowRef<
      | {
          name: string;
          icon?: Component;
          type?: ButtonType;
          tint?: Tint;
          func: () => void;
          disabled?: boolean;
        }[]
      | null
    > = shallowRef(null);
    const isDisplayed = ref(false);
    const releaseOverlayPresence = useOverlayPresence(isDisplayed);
    onBeforeUnmount(releaseOverlayPresence);
    const onClose: Ref<(() => void) | null> = ref(null);
    const isInputValid = ref(true);

    watch(inputValue, (newValue) => {
      if (input.value?.onChange) {
        input.value.onChange(newValue);
      }
      if (input.value?.onValidate) {
        isInputValid.value = input.value.onValidate(newValue);
      }
      inputError.value = false;
    });

    const showModal = (modalContent: SimpleModalContent) => {
      header.value = modalContent.header;
      text.value = modalContent.text;
      input.value = modalContent.input || null;
      inputValue.value = modalContent.input?.initialValue || "";
      inputError.value = false;
      if (modalContent.input?.onValidate) {
        isInputValid.value = modalContent.input.onValidate(
          modalContent.input.initialValue || ""
        );
      } else {
        isInputValid.value = true;
      }
      actions.value = modalContent.actions
        ? modalContent.withCancelButton
          ? [
              ...modalContent.actions,
              {
                name: "Cancel",
                type: "secondary",
                func: () => {},
              },
            ]
          : modalContent.actions
        : null;
      onClose.value = modalContent.onCloseFunc
        ? modalContent.onCloseFunc
        : null;
      isDisplayed.value = true;
    };

    const close = () => {
      if (onClose.value) onClose.value();
      isDisplayed.value = false;
    };

    const validatePrimaryAction = () => {
      if (!isInputValid.value) {
        return;
      }
      const primaryActions = actions.value?.filter(
        (action) => action.type === "primary"
      );
      if (primaryActions && primaryActions[0]) primaryActions[0].func();
      close();
    };
    const modalShortcuts = computed<ShortcutList>(() => [
      { keys: validateForm, action: validatePrimaryAction },
    ]);
    return {
      actions,
      close,
      header,
      input,
      inputValue,
      inputError,
      isDisplayed,
      isInputValid,
      modalActions,
      modalShortcuts,
      showModal,
      text,
      validatePrimaryAction,
    };
  },
});
</script>

<style lang="scss" scoped>
.simple-modal {
  .title {
    margin-inline-start: 4px;
  }

  p {
    margin-bottom: 16px;
  }

  .form-input {
    margin-bottom: 24px;
    width: 100%;
  }

  .buttons {
    display: flex;
    flex-direction: row-reverse;
    column-gap: 8px;
    margin-top: 8px;
  }
}
</style>
